import React from 'react';
import styled from 'styled-components';
import { colors, borderRadius, shadows } from '../styles/designTokens';
import { PointHistory } from '../models/CustomerModels';
import { formatDateTimeKST } from '../utils/dateUtils';

// 테이블 컨테이너 스타일
const TableContainer = styled.div`
  background: ${colors.semantic.background.primary};
  border-radius: ${borderRadius.xl};
  border: 1px solid ${colors.neutral[200]};
  overflow: hidden;
  box-shadow: ${shadows.base};
`;

// 테이블 스타일
const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
  
  th,
  td {
    padding: 16px 20px;
    text-align: left;
    border-bottom: 1px solid ${colors.neutral[100]};
  }

  th {
    background: ${colors.neutral[50]};
    font-weight: 600;
    color: ${colors.semantic.text.secondary};
    font-size: 13px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    border-bottom: 2px solid ${colors.neutral[200]};
  }

  tbody tr {
    transition: background-color 0.2s;

    &:hover {
      background: ${colors.neutral[50]};
    }
    
    &:last-child td {
      border-bottom: none;
    }
  }
  
  @media (max-width: 768px) {
    font-size: 12px;
    
    th, td {
      padding: 12px 16px;
    }
  }
`;

// 로딩 컨테이너 스타일
const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
  background: ${colors.semantic.background.primary};
  border-radius: ${borderRadius.xl};
  border: 1px solid ${colors.neutral[200]};
`;

// 로딩 스피너 스타일
const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid ${colors.neutral[100]};
  border-top: 3px solid ${colors.primary[500]};
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

// 빈 상태 컨테이너 스타일
const EmptyContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  padding: 40px 20px;
  text-align: center;
  background: ${colors.semantic.background.primary};
  border-radius: ${borderRadius.xl};
  border: 1px solid ${colors.neutral[200]};

  .icon {
    font-size: 48px;
    margin-bottom: 16px;
    opacity: 0.5;
    color: ${colors.semantic.text.disabled};
  }

  h3 {
    font-size: 18px;
    font-weight: 600;
    margin: 0 0 8px 0;
    color: ${colors.semantic.text.secondary};
  }

  p {
    font-size: 14px;
    margin: 0;
    color: ${colors.semantic.text.tertiary};
    line-height: 1.5;
  }
`;

// 날짜 텍스트 스타일
const DateText = styled.span`
  font-size: 12px;
  color: ${colors.semantic.text.tertiary};
  font-weight: 500;
`;

// 설명 텍스트 스타일
const DescriptionText = styled.span`
  font-weight: 500;
  color: ${colors.semantic.text.secondary};
`;

// 유형 배지 스타일
const TypeBadge = styled.span<{ type: string }>`
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border: 1px solid;
  
  ${props => {
    switch (props.type) {
      case '차감':
        return `
          color: ${colors.error[600]};
          background-color: ${colors.error[50]};
          border-color: ${colors.error[200]};
        `;
      case '적립':
        return `
          color: ${colors.success[600]};
          background-color: ${colors.success[50]};
          border-color: ${colors.success[200]};
        `;
      default:
        return `
          color: ${colors.semantic.text.tertiary};
          background-color: ${colors.neutral[50]};
          border-color: ${colors.neutral[200]};
        `;
    }
  }}
`;

// 포인트 텍스트 스타일
const PointText = styled.span<{ type: string }>`
  font-weight: 700;
  font-size: 14px;
  
  ${props => {
    switch (props.type) {
      case '차감':
        return `color: ${colors.error[600]};`;
      case '적립':
        return `color: ${colors.success[600]};`;
      default:
        return `color: ${colors.semantic.text.secondary};`;
    }
  }}
`;

// 잔여포인트 텍스트 스타일
const BalanceText = styled.span`
  font-weight: 600;
  color: ${colors.primary[500]};
  font-size: 14px;
`;

// 모바일에서만 테이블 좌우 스크롤 래퍼
const MobileTableScrollWrapper = styled.div`
  @media (max-width: 768px) {
    width: 100%;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    table {
      min-width: 600px;
      width: 100%;
    }
    th, td {
      white-space: nowrap;
    }
  }
`;

// 포인트 내역 테이블 Props 인터페이스
interface PointHistoryTableProps {
  data: PointHistory[];
  loading?: boolean;
}

// 날짜 포맷팅 함수는 utils/dateUtils.ts로 이동됨

// 포인트 포맷팅 함수 (천 단위 콤마 추가)
const formatPoints = (points: number): string => {
  return points.toLocaleString('ko-KR');
};

// 포인트 내역 테이블 컴포넌트
const PointHistoryTable: React.FC<PointHistoryTableProps> = ({ data, loading = false }) => {
  // 로딩 상태 표시
  if (loading) {
    return (
      <LoadingContainer>
        <LoadingSpinner />
      </LoadingContainer>
    );
  }

  // 데이터가 없는 경우
  if (!data || data.length === 0) {
    return (
      <EmptyContainer>
        <div className="icon">📊</div>
        <h3>포인트 내역이 없습니다</h3>
        <p>아직 포인트 사용 또는 적립 내역이 없습니다.</p>
      </EmptyContainer>
    );
  }

  return (
    <TableContainer>
      {/* 모바일에서만 좌우 스크롤 가능하도록 래퍼로 감쌈 */}
      <MobileTableScrollWrapper>
        <StyledTable>
          <thead>
            <tr>
              <th style={{ width: '20%' }}>생성일자</th>
              <th style={{ width: '35%' }}>설명</th>
              <th style={{ width: '10%' }}>유형</th>
              <th style={{ width: '15%' }}>포인트</th>
              <th style={{ width: '20%' }}>잔여포인트</th>
            </tr>
          </thead>
          <tbody>
            {data.map((item, index) => (
              <tr key={index}>
                <td>
                  <DateText>
                    {formatDateTimeKST(item.생성일자)}
                  </DateText>
                </td>
                <td>
                  <DescriptionText>
                    {item.설명}
                  </DescriptionText>
                </td>
                <td>
                  <TypeBadge type={item.유형}>
                    {item.유형}
                  </TypeBadge>
                </td>
                <td>
                  <PointText type={item.유형}>
                    {item.유형 === '차감' ? '-' : '+'}{formatPoints(item.포인트)}
                  </PointText>
                </td>
                <td>
                  <BalanceText>
                    {formatPoints(item.잔여포인트)}
                  </BalanceText>
                </td>
              </tr>
            ))}
          </tbody>
        </StyledTable>
      </MobileTableScrollWrapper>
    </TableContainer>
  );
};

export default PointHistoryTable; 